import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SendIcon, CheckCircleIcon } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
export function ContactForm() {
  const [state, handleSubmit] = useForm('xdkpvlla');
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    country: '',
    products: [] as string[],
    volume: '',
    message: ''
  });
  const productOptions = ['Hardware Wallets & Signers', 'Faraday Sleeves & Bags', 'USB Data Blockers', 'Encrypted Storage Devices', 'Other Security Hardware'];
  const handleProductToggle = (product: string) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(product) ? prev.products.filter(p => p !== product) : [...prev.products, product]
    }));
  };
  if (state.succeeded) {
    return <section id="contact" className="py-20 px-6 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)'
    }} data-id="element-52">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-53" />
        <div className="max-w-2xl mx-auto relative z-10" data-id="element-54">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white rounded-2xl p-12 text-center shadow-lg" data-id="element-55">
            <CheckCircleIcon className="w-16 h-16 text-[#10B981] mx-auto mb-6" data-id="element-56" />
            <h3 className="text-3xl font-bold text-[#0B1220] mb-4" data-id="element-57">
              Thank You!
            </h3>
            <p className="text-lg text-gray-600 mb-2" data-id="element-58">
              Your request has been received.
            </p>
            <p className="text-gray-600" data-id="element-59">
              We will share our current line sheet and MOQs within one business
              day.
            </p>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="contact" className="py-20 px-6 relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)'
  }} data-id="element-60">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1600&q=80)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} data-id="element-61" />
      {/* Lock Pattern Overlay */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80)',
      backgroundSize: '300px',
      backgroundRepeat: 'repeat'
    }} data-id="element-62" />
      <div className="max-w-3xl mx-auto relative z-10" data-id="element-63">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-64">
          <h2 className="text-4xl font-bold text-white mb-4" data-id="element-65">
            Get Line Sheet & Pricing
          </h2>
          <p className="text-lg text-white/90" data-id="element-66">
            Tell us what you need. We will share our current line sheet and MOQs
            within one business day.
          </p>
        </motion.div>
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20" data-id="element-67">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-68">
            <div data-id="element-69">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-70">
                Company Name *
              </label>
              <input type="text" name="company" required value={formData.company} onChange={e => setFormData({
              ...formData,
              company: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" placeholder="Your Company" data-id="element-71" />
              <ValidationError prefix="Company" field="company" errors={state.errors} data-id="element-72" />
            </div>
            <div data-id="element-73">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-74">
                Your Name *
              </label>
              <input type="text" name="name" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" placeholder="John Smith" data-id="element-75" />
              <ValidationError prefix="Name" field="name" errors={state.errors} data-id="element-76" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-77">
            <div data-id="element-78">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-79">
                Work Email *
              </label>
              <input type="email" name="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" placeholder="you@company.com" data-id="element-80" />
              <ValidationError prefix="Email" field="email" errors={state.errors} data-id="element-81" />
            </div>
            <div data-id="element-82">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-83">
                Phone (Optional)
              </label>
              <input type="tel" name="phone" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" placeholder="+1 234 567 8900" data-id="element-84" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-85">
            <div data-id="element-86">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-87">
                Country *
              </label>
              <input type="text" name="country" required value={formData.country} onChange={e => setFormData({
              ...formData,
              country: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" placeholder="United Arab Emirates" data-id="element-88" />
              <ValidationError prefix="Country" field="country" errors={state.errors} data-id="element-89" />
            </div>
            <div data-id="element-90">
              <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-91">
                Monthly Volume *
              </label>
              <select name="volume" required value={formData.volume} onChange={e => setFormData({
              ...formData,
              volume: e.target.value
            })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors" data-id="element-92">
                <option value="" data-id="element-93">Select volume</option>
                <option value="100-500" data-id="element-94">100-500 units</option>
                <option value="500-1000" data-id="element-95">500-1,000 units</option>
                <option value="1000-5000" data-id="element-96">1,000-5,000 units</option>
                <option value="5000+" data-id="element-97">5,000+ units</option>
              </select>
              <ValidationError prefix="Volume" field="volume" errors={state.errors} data-id="element-98" />
            </div>
          </div>
          <div className="mb-6" data-id="element-99">
            <label className="block text-sm font-semibold text-[#0B1220] mb-3" data-id="element-100">
              Interested Products *
            </label>
            <input type="hidden" name="products" value={formData.products.join(', ')} data-id="element-101" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-102">
              {productOptions.map(product => <label key={product} className="flex items-center gap-3 cursor-pointer" data-id="element-103">
                  <input type="checkbox" checked={formData.products.includes(product)} onChange={() => handleProductToggle(product)} className="w-5 h-5 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6]" data-id="element-104" />
                  <span className="text-sm text-gray-700" data-id="element-105">{product}</span>
                </label>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-106">
            <label className="block text-sm font-semibold text-[#0B1220] mb-2" data-id="element-107">
              Message
            </label>
            <textarea name="message" value={formData.message} onChange={e => setFormData({
            ...formData,
            message: e.target.value
          })} rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3B82F6] focus:outline-none transition-colors resize-none" placeholder="Tell us about your requirements..." data-id="element-108" />
            <ValidationError prefix="Message" field="message" errors={state.errors} data-id="element-109" />
          </div>
          <button type="submit" disabled={state.submitting} className="w-full px-8 py-4 bg-[#3B82F6] text-white rounded-xl text-lg font-semibold hover:bg-[#2563EB] transition-all hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-110">
            {state.submitting ? 'Sending...' : 'Get Line Sheet'}
            <SendIcon className="w-5 h-5" data-id="element-111" />
          </button>
        </motion.form>
      </div>
    </section>;
}